import { IcoEyeCrossed16 } from '@onefootprint/icons';
import type { ApiNodeDataSelfie, FaceDetectionMode, SelfieOutputKind, VideoCaptureMode } from '@onefootprint/request-types/dashboard';
import { Divider, Form, Tooltip } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import OutputMappings from '../../../shared/output-mappings';
import FaceComparisonSection from './face-comparison-section';
import type { FormData } from './selfie.types';
type SelfieFormProps = {
  node: NodeProps<ApiNodeDataSelfie>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
  isFirmEmployee: boolean;
};

/** The selfie image is always emitted, so its row is always shown and can't be removed. */
const PINNED_OUTPUT_KINDS: SelfieOutputKind[] = ['image'];
const DEFAULT_SIMILARITY_THRESHOLD = 80;
const DEFAULT_COMPARISON_KEY = 'selfie_face_comparison';
const faceDetectionMode = ({
  detectFaces,
  requireDetection
}: FormData): FaceDetectionMode => {
  if (!detectFaces) return 'off';
  return requireDetection ? 'required' : 'best_effort';
};
const videoCaptureMode = ({
  recordVideo,
  requireVideo
}: FormData): VideoCaptureMode => {
  if (!recordVideo) return 'off';
  return requireVideo ? 'required' : 'best_effort';
};
const SelfieForm = ({
  node: {
    id,
    data: {
      requireFaceDetection,
      allowMultipleFaces,
      videoCapture,
      comparison,
      outputs
    }
  },
  mutation,
  canEdit,
  isFirmEmployee
}: SelfieFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'selfie'
  });
  const {
    toggle
  } = useDirty();
  const form = useForm<FormData>({
    disabled: !canEdit,
    defaultValues: {
      detectFaces: requireFaceDetection !== 'off',
      requireDetection: requireFaceDetection === 'required',
      blockMultipleFaces: allowMultipleFaces === 'blocked',
      recordVideo: videoCapture === 'best_effort' || videoCapture === 'required',
      requireVideo: videoCapture === 'required',
      compareFaces: !!comparison,
      reference: comparison?.reference.expression ?? '',
      similarityThreshold: comparison?.similarityThreshold ?? DEFAULT_SIMILARITY_THRESHOLD,
      outputs: [...PINNED_OUTPUT_KINDS.map(kind => ({
        fieldKey: kind,
        onboardingDataKey: outputs.find(output => output.kind === kind)?.onboardingDataKey ?? ''
      })), ...outputs.filter(({
        kind: kind_0
      }) => !PINNED_OUTPUT_KINDS.includes(kind_0)).map(({
        kind: kind_1,
        onboardingDataKey
      }) => ({
        fieldKey: kind_1,
        onboardingDataKey
      }))]
    }
  });
  const {
    handleSubmit,
    formState
  } = form;
  const detectFaces = useWatch({
    control: form.control,
    name: 'detectFaces'
  });
  const compareFaces = useWatch({
    control: form.control,
    name: 'compareFaces'
  });
  const recordVideo = useWatch({
    control: form.control,
    name: 'recordVideo'
  });
  const outputKinds: {
    value: SelfieOutputKind;
    label: string;
  }[] = [{
    value: 'image',
    label: t($ => $.outputs.kinds.image)
  }, ...(detectFaces ? [{
    value: 'face_detection' as const,
    label: t($_0 => $_0.outputs.kinds.faceDetection)
  }] : []), ...(compareFaces ? [{
    value: 'face_comparison' as const,
    label: t($_1 => $_1.outputs.kinds.faceComparison)
  }] : [])];
  const pinnedOutputKinds: SelfieOutputKind[] = [...PINNED_OUTPUT_KINDS, ...(compareFaces ? ['face_comparison' as const] : [])];

  // Detection that's off writes no output, so the backend rejects a mapping for one.
  const handleDetectionToggle = (checked: boolean) => {
    if (checked) return;
    const remaining = form.getValues('outputs').filter(output_0 => output_0.fieldKey !== 'face_detection');
    form.setValue('outputs', remaining, {
      shouldDirty: true
    });
  };

  // The backend requires a comparison output while comparison is on and rejects one while it's off.
  const handleComparisonToggle = (checked_0: boolean) => {
    const remaining_0 = form.getValues('outputs').filter(output_1 => output_1.fieldKey !== 'face_comparison');
    const comparisonOutput = {
      fieldKey: 'face_comparison',
      onboardingDataKey: DEFAULT_COMPARISON_KEY
    };
    form.setValue('outputs', checked_0 ? [...remaining_0, comparisonOutput] : remaining_0, {
      shouldDirty: true
    });
  };
  useEffect(() => {
    toggle('selfie-form', formState.isDirty);
  }, [formState.isDirty]);
  const handleFormSubmit = async (data: FormData) => {
    try {
      await mutation.mutateAsync({
        kind: 'update_node',
        nodeId: id,
        nodeData: {
          kind: 'selfie',
          requireFaceDetection: faceDetectionMode(data),
          // No detection => nothing to count faces against, so multiple-faces blocking is moot.
          allowMultipleFaces: data.detectFaces && data.blockMultipleFaces ? 'blocked' : 'allowed',
          videoCapture: videoCaptureMode(data),
          comparison: data.compareFaces ? {
            reference: {
              kind: comparison?.reference.kind ?? 'front_image',
              expression: data.reference
            },
            similarityThreshold: data.similarityThreshold ?? DEFAULT_SIMILARITY_THRESHOLD
          } : undefined,
          outputs: data.outputs.map(({
            fieldKey,
            onboardingDataKey: onboardingDataKey_0
          }) => ({
            kind: fieldKey as SelfieOutputKind,
            onboardingDataKey: onboardingDataKey_0
          }))
        }
      });
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  return <FormProvider {...form}>
      <form id="selfie-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4">
          {isFirmEmployee && <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-label-2 text-primary">{t($_2 => $_2.faceDetection.requirement.label)}</span>
                  <Tooltip content={t($_3 => $_3.faceDetection.employeeOnly)} side="left">
                    <div className="flex-shrink-0 text-tertiary">
                      <IcoEyeCrossed16 />
                    </div>
                  </Tooltip>
                </div>
                <span className="text-body-2 text-secondary">{t($_4 => $_4.faceDetection.requirement.subtitle)}</span>
              </div>
              <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">
                <Form.Toggle<FormData> name="detectFaces" label={t($_5 => $_5.faceDetection.toggle)} size="compact" onChange={handleDetectionToggle} />
                {detectFaces && <>
                    <Divider />
                    <Form.Toggle<FormData> name="requireDetection" label={t($_6 => $_6.faceDetection.require)} hint={t($_7 => $_7.faceDetection.requireHint)} size="compact" />
                    <Form.Toggle<FormData> name="blockMultipleFaces" label={t($_8 => $_8.faceDetection.blockMultipleFaces)} hint={t($_9 => $_9.faceDetection.blockMultipleFacesHint)} size="compact" />
                  </>}
              </div>
            </section>}
          {isFirmEmployee && <FaceComparisonSection nodeId={id} onToggle={handleComparisonToggle} />}
          {isFirmEmployee && <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-label-2 text-primary">{t($_10 => $_10.video.title)}</span>
                  <Tooltip content={t($_11 => $_11.video.employeeOnly)} side="left">
                    <div className="flex-shrink-0 text-tertiary">
                      <IcoEyeCrossed16 />
                    </div>
                  </Tooltip>
                </div>
                <span className="text-body-2 text-secondary">{t($_12 => $_12.video.subtitle)}</span>
              </div>
              <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">
                <Form.Toggle<FormData> name="recordVideo" label={t($_13 => $_13.video.toggle)} size="compact" />
                {recordVideo && <>
                    <Divider />
                    <Form.Toggle<FormData> name="requireVideo" label={t($_14 => $_14.video.require)} hint={t($_15 => $_15.video.requireHint)} size="compact" />
                  </>}
              </div>
            </section>}
          <OutputMappings canEdit={canEdit} subtitle={t($_16 => $_16.outputs.subtitle)} options={outputKinds} outputLabel={t($_17 => $_17.outputs.outputLabel)} pinnedFieldKeys={pinnedOutputKinds} />
        </div>
      </form>
    </FormProvider>;
};
export default SelfieForm;